import { useCallback, useMemo } from "react";
import { useHomeContextUser } from "../../../homeContext.ts";
import {
  isUnlimitedUserCredit,
  hasUnlimitedCopies,
  mapCreditsByIds,
} from "./utils";

export enum BorrowStatus {
  Borrowed = "borrowed",
  Borrowable = "borrowable",
  NoCredits = "no-credits",
  NoCopies = "no-copies",
  NoCreditsOrCopies = "no-credits-or-copies",
  UnlimitedCreditsAndCopies = "unlimited-credits-and-copies",
  UnBorrowable = "unborrowable",
}

function useAssetAvailability(asset?: VST.Bridge.Asset) {
  const user = useHomeContextUser();

  const getMatchingCredits = useCallback((): {
    matchingUserCredit: VST.Bridge.UserCredit;
    matchingAssetCredit: VST.Bridge.AssetCredit;
  } => {
    const assetCreditList = asset?.creditList ?? [];
    const userCreditList = user.creditList;

    const assetCreditListById = mapCreditsByIds(assetCreditList);
    const userCreditListById = mapCreditsByIds(userCreditList);

    const matchingCreditId = Object.keys(userCreditListById).filter((id) =>
      Object.keys(assetCreditListById).includes(id)
    )[0];
    const matchingUserCredit = userCreditListById[
      matchingCreditId
    ] as VST.Bridge.UserCredit;
    const matchingAssetCredit = assetCreditListById[
      matchingCreditId
    ] as VST.Bridge.AssetCredit;
    return {
      matchingUserCredit,
      matchingAssetCredit,
    };
  }, [asset, user]);

  const assetHasUnlimitedCopies = useMemo(() => {
    const assetCredit = getMatchingCredits().matchingAssetCredit;
    return hasUnlimitedCopies(assetCredit);
  }, [getMatchingCredits]);

  const copiesAvailable = useMemo((): boolean => {
    const assetCredit = getMatchingCredits().matchingAssetCredit;
    return !!assetCredit?.copies && assetCredit.copies.available > 0;
  }, [getMatchingCredits]);

  const userHasUnlimitedCreditsForAsset = useMemo((): boolean => {
    const userCredit = getMatchingCredits().matchingUserCredit;
    return isUnlimitedUserCredit(userCredit);
  }, [getMatchingCredits]);

  const creditsAvailable = useMemo((): boolean => {
    const userCredit = getMatchingCredits().matchingUserCredit;
    return (
      userHasUnlimitedCreditsForAsset ||
      (!!userCredit?.credits && userCredit.credits.available > 0)
    );
  }, [getMatchingCredits, userHasUnlimitedCreditsForAsset]);

  const getBorrowStatus = useCallback((): BorrowStatus => {
    if (!asset?.active && !asset?.distributable) {
      return BorrowStatus.UnBorrowable;
    }
    if (asset?.active) {
      return BorrowStatus.Borrowed;
    }

    // Note that credit-related availability is not computable once an asset has been
    // activated. Activated assets no longer have asset credit information.

    if (!creditsAvailable && !copiesAvailable && !assetHasUnlimitedCopies) {
      return BorrowStatus.NoCreditsOrCopies;
    }
    if (!creditsAvailable) {
      return BorrowStatus.NoCredits;
    }
    if (!copiesAvailable && !assetHasUnlimitedCopies) {
      return BorrowStatus.NoCopies;
    }
    if (assetHasUnlimitedCopies && userHasUnlimitedCreditsForAsset) {
      return BorrowStatus.UnlimitedCreditsAndCopies;
    }
    return BorrowStatus.Borrowable;
  }, [
    asset,
    creditsAvailable,
    copiesAvailable,
    assetHasUnlimitedCopies,
    userHasUnlimitedCreditsForAsset,
  ]);

  return {
    getBorrowStatus,
    getMatchingCredits,
    creditsAvailable,
  };
}

export default useAssetAvailability;
